import React, { useMemo, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';

// We store invoice number as ONE string, only letters/numbers/spaces/hyphen.
// Separator between segments in the stored value:
const SEPARATOR = ' - ';
const INVOICE_NUMBER_FORMAT =
  'Invoice Approval - FTTS-city name / NonFTTS-section name - Year PO Number - OWN/IRU(Onnet-Offnet) - Vendor Name - Region - Sequence(001)';

const SEGMENT_COUNT = 7;
const SEGMENT_KEYS = [
  'invoiceApproval',
  'sectionName',
  'yearPoNumber',
  'ownIru',
  'vendorName',
  'region',
  'sequence',
];
const FIXED_INVOICE_APPROVAL_TEXT = 'Invoice Approval';
const getFromToPrefix = (invoiceType) =>
  String(invoiceType || '')
    .toLowerCase()
    .includes('non')
    ? 'NonFTTS-'
    : 'FTTS-';

const stripKnownFromToPrefixes = (value) => {
  const v = String(value || '');
  return v.replace(/^(FTTS-|NonFTTS-)/i, '');
};

const getNetworkTypePrefix = (invoiceType) =>
  String(invoiceType || '')
    .toLowerCase()
    .includes('non')
    ? 'NonFTTS-'
    : 'FTTS-';
const REGION_OPTIONS = ['North', 'South', 'Central A', 'Central B'];

const sanitizeSegment = (value) => {
  const raw = String(value ?? '');
  // Replace any special chars (%, /, ||, etc.) with spaces, keep only letters/numbers/spaces/hyphen
  const cleaned = raw.replace(/[^a-zA-Z0-9 -]+/g, ' ');
  // Collapse whitespace but DO NOT touch internal hyphens, so values like \"10744-PMCL\" stay intact
  return cleaned.replace(/\s+/g, ' ').trim();
};

// For live typing: allow spaces (including trailing) but remove disallowed characters.
const sanitizeSegmentForInput = (value) => {
  const raw = String(value ?? '');
  const cleaned = raw.replace(/[^a-zA-Z0-9 -]+/g, ' ');
  // Collapse multiple spaces but do NOT trim so spacebar feels normal while typing
  return cleaned.replace(/\s{2,}/g, ' ');
};

// Sequence segment (e.g. 001): digits only
const sanitizeSequenceForInput = (value) => String(value ?? '').replace(/\D/g, '');

const parseInvoiceNumber = (value) => {
  if (!value || typeof value !== 'string') {
    const empty = Array(SEGMENT_COUNT).fill('');
    empty[0] = FIXED_INVOICE_APPROVAL_TEXT;
    return empty;
  }

  // Backward compatibility: previously used " || "
  const normalized = value.includes(' || ') ? value.split(' || ').join(SEPARATOR) : value;
  let parts = normalized.split(SEPARATOR).map((s) => sanitizeSegment(s));
  // Legacy 8-part format included a Fiber Milestone segment at index 3; omit it when reading
  if (parts.length >= 8) {
    parts = [...parts.slice(0, 3), ...parts.slice(4)];
  }
  const result = [...parts];
  while (result.length < SEGMENT_COUNT) {
    result.push('');
  }
  result[0] = FIXED_INVOICE_APPROVAL_TEXT;
  if (result[6]) {
    result[6] = sanitizeSequenceForInput(result[6]);
  }
  return result.slice(0, SEGMENT_COUNT);
};

const sanitizeSegmentForPersist = (value, index) => {
  const cleaned = sanitizeSegment(value);
  return index === 6 ? sanitizeSequenceForInput(cleaned) : cleaned;
};

const InvoiceDetailsSection = ({ formData, onInputChange }) => {
  const segments = useMemo(
    () => parseInvoiceNumber(formData.invoiceNumber),
    [formData.invoiceNumber]
  );

  const [localSegments, setLocalSegments] = useState(segments);
  const internalUpdateRef = useRef(false);

  useEffect(() => {
    // Avoid overwriting user typing on every keystroke (we store trimmed value in formData)
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false;
      return;
    }
    setLocalSegments(parseInvoiceNumber(formData.invoiceNumber));
  }, [formData.invoiceNumber]);

  const updateSegment = (index, value) => {
    // Segment 0 is fixed text
    // Segment 3 is auto-filled from Type of Invoice + Type selection
    if (index === 0 || index === 3) {
      return;
    }
    const prefix = index === 1 ? getFromToPrefix(formData.invoiceType) : '';
    const rawValue = index === 1 ? stripKnownFromToPrefixes(value) : value;
    const sanitized =
      index === 6 ? sanitizeSequenceForInput(rawValue) : sanitizeSegmentForInput(rawValue);
    const next = [...localSegments];
    next[index] = index === 1 ? `${prefix}${sanitized}` : sanitized;
    next[0] = FIXED_INVOICE_APPROVAL_TEXT;
    setLocalSegments(next);
    // Persist a trimmed + fully sanitized string
    const joined = next.map((s, i) => sanitizeSegmentForPersist(s, i)).join(SEPARATOR);
    internalUpdateRef.current = true;
    onInputChange('invoice', 'invoiceNumber', joined);
    if (index === 5) {
      onInputChange('additional', 'region', sanitizeSegment(value));
    }
  };

  // Suggest empty segments from form data when PO/Vendor/Region/Type/Ring data is available
  useEffect(() => {
    const parsed = parseInvoiceNumber(formData.invoiceNumber);
    const next = [...parsed];
    let changed = false;
    if (!parsed[2]?.trim() && formData.poNumber?.trim()) {
      next[2] = formData.poNumber.trim();
      changed = true;
    }
    // OWN/IRU(Onnet-Offnet) segment must always match Type of Invoice + Type selection
    const desiredNetworkType = `${getNetworkTypePrefix(formData.invoiceType)}${sanitizeSegment(
      formData.type || ''
    )}`;
    if ((parsed[3] || '') !== desiredNetworkType) {
      next[3] = desiredNetworkType;
      changed = true;
    }
    if (!parsed[4]?.trim() && formData.vendor?.trim()) {
      next[4] = formData.vendor.trim();
      changed = true;
    }
    // Region segment should match Region selection
    const selectedRegion = sanitizeSegment(formData.region || '');
    if ((parsed[5] || '') !== selectedRegion) {
      next[5] = selectedRegion;
      changed = true;
    }
    // From-To segment: keep prefix (FTTS-/NonFTTS-) always, user fills suffix (city or section name)
    const fromToPrefix = getFromToPrefix(formData.invoiceType);
    const currentFromToSuffix = stripKnownFromToPrefixes(parsed[1] || '').trim();
    const desiredFromTo = `${fromToPrefix}${sanitizeSegment(currentFromToSuffix)}`;
    if ((parsed[1] || '') !== desiredFromTo) {
      next[1] = desiredFromTo;
      changed = true;
    }
    if (changed) {
      next[0] = FIXED_INVOICE_APPROVAL_TEXT;
      const joined = next.map((s, i) => sanitizeSegmentForPersist(s, i)).join(SEPARATOR);
      onInputChange('invoice', 'invoiceNumber', joined);
      setLocalSegments(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only suggest when PO/vendor/region/type change
  }, [formData.poNumber, formData.type, formData.vendor, formData.region]);

  // If invoice type toggles, update From-To prefix while keeping the typed suffix
  useEffect(() => {
    setLocalSegments((prev) => {
      const next = [...prev];
      const suffix = stripKnownFromToPrefixes(next[1] || '').trim();
      next[1] = `${getFromToPrefix(formData.invoiceType)}${suffix}`;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.invoiceType]);

  const allFilled = useMemo(
    () => localSegments.every((s) => s != null && String(s).trim() !== ''),
    [localSegments]
  );
  const hasValue = useMemo(
    () => localSegments.some((s) => s != null && String(s).trim() !== ''),
    [localSegments]
  );
  const showError = hasValue && !allFilled;

  const segmentLabels = [
    'Invoice Approval',
    'From-To',
    'Year/PO Number',
    'OWN/IRU(Onnet-Offnet)',
    'Vendor Name',
    'Region',
    'Sequence',
  ];
  const placeholders = [
    'e.g. Invoice Approval',
    formData.invoiceType === 'Non-FTTS' ? 'e.g. NonFTTS-section name' : 'e.g. FTTS-city name',
    formData.poNumber ? `e.g. ${formData.poNumber}` : 'e.g. 2024 10744-PMCL',
    'e.g. Onnet / Offnet',
    formData.vendor ? `e.g. ${formData.vendor}` : 'Vendor Name',
    formData.region ? `e.g. ${formData.region}` : 'Region',
    'e.g. 001',
  ];

  return (
    <Box sx={{ mb: 4 }}>
      {/* Type of Invoice */}
      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontSize: '16px',
            fontFamily: 'Inter',
            fontWeight: 600,
            mb: 1,
            display: 'flex',
          }}
        >
          Type of Invoice
        </Typography>
        <RadioGroup
          row
          value={formData.invoiceType}
          onChange={(e) => onInputChange('invoice', 'invoiceType', e.target.value)}
        >
          <FormControlLabel value="FTTS" control={<Radio />} label="FTTS" />
          <FormControlLabel value="Non-FTTS" control={<Radio />} label="Non-FTTS" />
        </RadioGroup>
        <Box sx={{ mt: 2, maxWidth: '300px' }}>
          <FormControl fullWidth size="small">
            <InputLabel>Type *</InputLabel>
            {formData.invoiceType === 'Non-FTTS' ? (
              <Select
                value={formData.type}
                label="Type *"
                onChange={(e) => onInputChange('invoice', 'type', e.target.value)}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'left',
                  },
                }}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Own-fiber">Own-Fiber</MenuItem>
                <MenuItem value="IRU-OffNet">IRU-Offnet</MenuItem>
                <MenuItem value="IRU-Onnet">IRU-Onnet</MenuItem>
              </Select>
            ) : (
              <Select
                value={formData.type}
                label="Type *"
                onChange={(e) => onInputChange('invoice', 'type', e.target.value)}
                sx={{
                  '& .MuiSelect-select': {
                    textAlign: 'left',
                  },
                }}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="On-Net">On-Net</MenuItem>
                <MenuItem value="Off-Net">Off-Net</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            )}
          </FormControl>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: '18px',
          fontFamily: 'Inter',
          fontWeight: 600,
          mb: 0.5,
          display: 'flex',
          mt: 2,
        }}
      >
        Invoice Number{' '}
        <Box component="span" sx={{ color: '#d32f2f' }}>
          *
        </Box>
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 1.5,
          fontFamily: 'monospace',
          fontSize: '0.7rem',
          lineHeight: 1.4,
        }}
      >
        {INVOICE_NUMBER_FORMAT}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 0,
          border: '1px solid',
          borderColor: showError ? '#d32f2f' : 'divider',
          borderRadius: 1,
          backgroundColor: showError ? 'rgba(211, 47, 47, 0.04)' : 'grey.50',
          p: 1.5,
          transition: 'border-color 0.2s, background-color 0.2s',
        }}
      >
        {SEGMENT_KEYS.map((_, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <Box
                sx={{
                  alignSelf: 'center',
                  px: 0.75,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              ></Box>
            )}
            {index === 3 ? (
              <TextField
                size="small"
                placeholder={
                  formData.invoiceType === 'Non-FTTS' ? 'NonFTTS-IRU-Offnet' : 'FTTS-Onnet'
                }
                value={localSegments[index] ?? ''}
                disabled
                sx={{
                  minWidth: { xs: '100%', sm: 120 },
                  flex: { xs: '1 1 100%', sm: '1 1 140px' },
                  maxWidth: { sm: 220 },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F9FAFB',
                    fontSize: '0.8rem',
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    opacity: 1,
                  },
                }}
                inputProps={{
                  'aria-label': segmentLabels[index],
                }}
              />
            ) : index === 5 ? (
              <FormControl
                size="small"
                sx={{
                  minWidth: { xs: '100%', sm: 120 },
                  flex: { xs: '1 1 100%', sm: '1 1 140px' },
                  maxWidth: { sm: 180 },
                }}
                error={showError && !localSegments[index]?.trim()}
              >
                <InputLabel>Region</InputLabel>
                <Select
                  value={localSegments[index] ?? ''}
                  label="Region"
                  onChange={(e) => updateSegment(index, e.target.value)}
                  sx={{
                    '& .MuiSelect-select': {
                      textAlign: 'left',
                      fontSize: '0.8rem',
                      backgroundColor: '#fff',
                    },
                  }}
                >
                  <MenuItem value="">Select</MenuItem>
                  {REGION_OPTIONS.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : index === 6 ? (
              <TextField
                size="small"
                placeholder={placeholders[index]}
                value={localSegments[index] ?? ''}
                onChange={(e) => updateSegment(index, e.target.value)}
                onKeyDown={(e) => {
                  const allowed = [
                    'Backspace',
                    'Delete',
                    'Tab',
                    'ArrowLeft',
                    'ArrowRight',
                    'Home',
                    'End',
                  ];
                  if (allowed.includes(e.key)) {
                    return;
                  }
                  if (e.ctrlKey || e.metaKey) {
                    return;
                  }
                  if (!/^\d$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '');
                  if (!pasted) {
                    return;
                  }
                  const current = sanitizeSequenceForInput(localSegments[index] ?? '');
                  updateSegment(index, `${current}${pasted}`.slice(0, 6));
                }}
                error={showError && !localSegments[index]?.trim()}
                sx={{
                  minWidth: { xs: '100%', sm: 120 },
                  flex: { xs: '1 1 100%', sm: '1 1 80px' },
                  maxWidth: { sm: 100 },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    fontSize: '0.8rem',
                  },
                }}
                inputProps={{
                  'aria-label': segmentLabels[index],
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 6,
                }}
              />
            ) : (
              <TextField
                size="small"
                placeholder={placeholders[index]}
                value={index === 0 ? FIXED_INVOICE_APPROVAL_TEXT : (localSegments[index] ?? '')}
                onChange={(e) => updateSegment(index, e.target.value)}
                error={index !== 0 && showError && !localSegments[index]?.trim()}
                disabled={index === 0}
                sx={{
                  minWidth: { xs: '100%', sm: 120 },
                  flex: { xs: '1 1 100%', sm: '1 1 140px' },
                  maxWidth: { sm: 180 },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: index === 0 ? '#F9FAFB' : '#fff',
                    fontSize: '0.8rem',
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    opacity: 1,
                  },
                }}
                inputProps={{
                  'aria-label': segmentLabels[index],
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>

      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
        Saved Invoice Number:{' '}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {(formData.invoiceNumber || '').trim() || '—'}
        </Box>
      </Typography>

      {showError && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#d32f2f' }}>
          Please fill all parts. Format: {INVOICE_NUMBER_FORMAT}
        </Typography>
      )}
    </Box>
  );
};

export default InvoiceDetailsSection;
